import type { SearchHit, SearchScoring, SearchStrategy } from '../types';

/**
 * Turn the backend's scoring payload into the factor list a result card
 * renders (OMA-639).
 *
 * The one rule: say what the payload says, and say nothing when it says
 * nothing. Every value here is a passthrough of a number the pipeline actually
 * produced — no averaging into a single "confidence", no filling a missing
 * factor with 0, no describing a stage that did not run. A missing
 * `score_breakdown` yields an empty explanation rather than a generic sentence.
 *
 * Pure and framework-free on purpose: the i18n keys are returned, not
 * resolved, so this is unit-testable without a React or next-intl runtime
 * (`tests/unit/search-score-explanation.test.ts`).
 */

export type ScoreFactorKind = 'semantic' | 'keyword' | 'rerank' | 'chunks';

export interface ScoreFactor {
  kind: ScoreFactorKind;
  /** Message key under `search.explain.*`. */
  messageKey: string;
  /** Interpolation values for that message. Only keys with real data. */
  values: Record<string, number>;
  /** Keyword factor only — the indexed fields that matched, with their weight. */
  fields?: { name: string; weight: number | null }[];
}

export interface ScoreExplanation {
  /**
   * The rate the detail panel reports — `hit.rank_score`, the value that
   * actually decided this card's position — or null when the pipeline produced
   * no such number (filter listings, degraded results, a page the reranker
   * never scored, a candidate it declined to rate). NOT the card's ring, which
   * shows the labelled cosine instead.
   */
  percent: number | null;
  strategy: SearchStrategy | null;
  /** Message key under `search.explain.*` describing the ordering itself. */
  rankingKey: string | null;
  rankingValues: Record<string, number>;
  factors: ScoreFactor[];
}

const RANKING_KEY_BY_STRATEGY: Record<SearchStrategy, string> = {
  hybrid: 'rankingHybrid',
  keyword: 'rankingKeyword',
  fallback: 'rankingFallback',
  filter: 'rankingFilter',
  definition_expansion: 'rankingDefinitionExpansion'
};

export function buildScoreExplanation(
  hit: SearchHit,
  scoring?: SearchScoring | null
): ScoreExplanation {
  const breakdown = hit.score_breakdown ?? null;

  if (!breakdown) {
    return {
      percent: null,
      strategy: null,
      rankingKey: null,
      rankingValues: {},
      factors: []
    };
  }

  const { strategy } = breakdown;

  return {
    // Read straight off the server, never re-derived here. The backend is the
    // one place that knows the settled order, and it only emits a number it
    // can defend as the thing that produced it — so the dialog's headline rate
    // and the rank meter beside it are the same value by construction instead
    // of by two implementations agreeing. The cosine (`semantic.similarity`) is
    // reported as its own factor below, but it is NOT the rate: nothing orders
    // by it. The card shows that cosine under a label; this panel owns the
    // ordering story and must not borrow the card's number.
    percent: hit.rank_score ?? null,
    strategy,
    // The tier count is part of the hybrid sentence, so a page that did not
    // report one gets the sentence without it rather than a message with an
    // unfilled placeholder (next-intl throws on those).
    rankingKey:
      strategy === 'hybrid' && scoring?.text_match_buckets == null
        ? 'rankingHybridPlain'
        : (RANKING_KEY_BY_STRATEGY[strategy] ?? null),
    rankingValues:
      strategy === 'hybrid' && scoring?.text_match_buckets != null
        ? { buckets: scoring.text_match_buckets }
        : {},
    factors: [
      semanticFactor(breakdown.semantic),
      keywordFactor(breakdown.keyword, scoring),
      rerankFactor(breakdown.rerank),
      chunkFactor(breakdown.chunks)
    ].filter((factor): factor is ScoreFactor => factor !== null)
  };
}

function semanticFactor(
  semantic: NonNullable<SearchHit['score_breakdown']>['semantic']
): ScoreFactor | null {
  if (!semantic || semantic.similarity == null) {
    return null;
  }

  return {
    kind: 'semantic',
    messageKey: 'semantic',
    values: {
      percent: semantic.similarity,
      distance: semantic.vector_distance
    }
  };
}

function keywordFactor(
  keyword: NonNullable<SearchHit['score_breakdown']>['keyword'],
  scoring?: SearchScoring | null
): ScoreFactor | null {
  if (!keyword) {
    return null;
  }

  const fields = (keyword.fields ?? []).map((name) => ({
    name,
    // The weight the query was SENT with, not one kept in the UI. Null when
    // the server did not report the weights.
    weight: scoring?.field_weights?.[name] ?? null
  }));

  // `tokens_matched` is the engine's own count; when it is absent, counting the
  // tokens the engine highlighted is the same fact read off a different field,
  // not an estimate. An EMPTY highlight list is "not reported", not "zero
  // matched" — the engine omits matched_tokens on some field types, and a hit
  // that matched nothing would not be a hit.
  const highlighted = keyword.matched_tokens?.length ?? 0;
  const tokens =
    keyword.tokens_matched ?? (highlighted > 0 ? highlighted : null);

  if (tokens == null && fields.length === 0) {
    return null;
  }

  // Same placeholder rule as the ranking sentence: no token count means a
  // message that does not interpolate one.
  if (tokens == null) {
    return {
      kind: 'keyword',
      messageKey: 'matchedInFields',
      values: {},
      fields
    };
  }

  return {
    kind: 'keyword',
    messageKey: fields.length > 0 ? 'keywordInFields' : 'keyword',
    values: { tokens },
    fields
  };
}

function rerankFactor(
  rerank: NonNullable<SearchHit['score_breakdown']>['rerank']
): ScoreFactor | null {
  if (!rerank) {
    return null;
  }

  // Silence is not a rating. A candidate the model never scored is reported as
  // exactly that — rendering it as 0 would read as "judged irrelevant".
  if (!rerank.scored || rerank.score == null) {
    return { kind: 'rerank', messageKey: 'rerankUnscored', values: {} };
  }

  const moved = rerank.from_position !== rerank.to_position;

  return {
    kind: 'rerank',
    messageKey: moved ? 'rerankMoved' : 'rerankHeld',
    values: moved
      ? {
          score: rerank.score,
          // The payload counts from 0; people count from 1.
          from: rerank.from_position + 1,
          to: rerank.to_position + 1
        }
      : { score: rerank.score, to: rerank.to_position + 1 }
  };
}

function chunkFactor(
  chunks: NonNullable<SearchHit['score_breakdown']>['chunks']
): ScoreFactor | null {
  if (!chunks || chunks.matched <= 0) {
    return null;
  }

  const pages = chunks.pages?.length ?? 0;

  // Passages matched by term were never scored by the vector half, so there is
  // no per-passage cosine to report and none is claimed.
  if (chunks.best_similarity == null) {
    return {
      kind: 'chunks',
      messageKey: 'chunks',
      values: { count: chunks.matched, pages }
    };
  }

  return {
    kind: 'chunks',
    messageKey: 'chunksWithBest',
    values: {
      count: chunks.matched,
      best: chunks.best_similarity,
      pages
    }
  };
}
